import {
    LoadingTypes,
    PriceConfig,
    PricingPlansMutations,
    PricingPlansState,
    ApiRoutes,
    TimeplanPriceConfig,
    PriceList,
    CountryDefaultConfig,
    AdjustmentBreakdownDto,
    SpotAdjustment,
} from "@/store/pricing-plans/types"
import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import { RootState } from "@/store"
import axios, { AxiosResponse } from "axios"

const state: PricingPlansState = {
    configList: [],
    countryDefaultConfigList: [],
    loading: {
        [LoadingTypes.CONFIG_LIST]: false,
        [LoadingTypes.COUNTRY_CONFIG_LIST]: false,
        [LoadingTypes.PRICE_LISTS]: false,
        [LoadingTypes.TIMEPLAN_CONFIG_LIST]: false,
        [LoadingTypes.PRICE_LIST]: false,
        [LoadingTypes.CONFIG_BY_ID]: false,
        [LoadingTypes.ADJUSTMENT_BREAKDOWN]: false,
    },
    timeplanConfigList: [],
    priceLists: [],
    priceList: null,
    editConfig: null,
    adjustmentBreakdown: null,
}

const mutations: MutationTree<PricingPlansState> = {
    setConfigList: (state, configList: PriceConfig[]) => {
        state.configList = configList
    },
    setLoading(state, payload: { type: LoadingTypes; loading: boolean }) {
        state.loading = {
            ...state.loading,
            [LoadingTypes[payload.type]]: payload.loading,
        }
    },
    setTimeplanConfigList: (state, timeplanConfigList: TimeplanPriceConfig[]) => {
        state.timeplanConfigList = timeplanConfigList
    },
    setPriceLists: (state, priceLists: PriceList[]) => {
        state.priceLists = priceLists
    },
    setPriceList: (state, priceList: PriceList) => {
        state.priceList = priceList
    },
    setEditConfig: (state, config: PriceConfig) => {
        state.editConfig = config
    },
    setCountryDefaultConfigList: (state, countryDefaultConfigList: CountryDefaultConfig[]) => {
        state.countryDefaultConfigList = countryDefaultConfigList || []
    },
    setAdjustmentBreakdown: (state, breakdown: AdjustmentBreakdownDto | null) => {
        state.adjustmentBreakdown = breakdown
    },
}

const actions: ActionTree<PricingPlansState, RootState> = {
    async fetchPriceLists({ commit, dispatch }) {
        commit(PricingPlansMutations.SET_LOADING, { type: LoadingTypes.PRICE_LISTS, loading: true })

        try {
            const res = await axios.get<AxiosResponse<{ priceLists: PriceList[] }>>(
                ApiRoutes.PRICE_LIST,
            )
            commit("setPriceLists", res.data.data.priceLists)
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.get_data_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.PRICE_LISTS,
                loading: false,
            })
        }
    },
    async fetchPriceListById({ commit, dispatch }, priceListId: number) {
        commit(PricingPlansMutations.SET_LOADING, { type: LoadingTypes.PRICE_LIST, loading: true })

        // /pricing/plans/
        try {
            const res = await axios.get<AxiosResponse<{ priceList: PriceList }>>(
                `${ApiRoutes.PRICE_LIST}/${priceListId}`,
            )
            commit("setPriceList", res.data.data.priceList)
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.get_data_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.PRICE_LIST,
                loading: false,
            })
        }
    },
    async fetchTimeplanConfigList({ commit, dispatch }, timeplanId: number) {
        //todo: not in use anymore??? check
        commit(PricingPlansMutations.SET_LOADING, {
            type: LoadingTypes.TIMEPLAN_CONFIG_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<AxiosResponse<PriceConfig[]>>(
                `${ApiRoutes.TIME_PLANS}/${timeplanId}${ApiRoutes.TP_CONFIGS}`,
            )
            commit("setTimeplanConfigList", res.data.data)
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.get_data_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.TIMEPLAN_CONFIG_LIST,
                loading: false,
            })
        }
    },
    async fetchConfigList({ commit, dispatch }, countryId?: number) {
        //todo: this will be us
        commit(PricingPlansMutations.SET_LOADING, { type: LoadingTypes.CONFIG_LIST, loading: true })

        try {
            const url = countryId
                ? `${ApiRoutes.CONFIGS}?countryId=${countryId}`
                : ApiRoutes.CONFIGS
            const res = await axios.get<AxiosResponse<PriceConfig[]>>(url)
            commit("setConfigList", res.data.data)
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.get_data_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.CONFIG_LIST,
                loading: false,
            })
        }
    },
    async fetchConfigById({ commit, dispatch }, configId: number) {
        commit(PricingPlansMutations.SET_LOADING, {
            type: LoadingTypes.CONFIG_BY_ID,
            loading: true,
        })

        try {
            const res = await axios.get<AxiosResponse<PriceConfig>>(
                `${ApiRoutes.CONFIGS}/${configId}`,
            )
            commit("pricingPlans/" + PricingPlansMutations.SET_EDIT_CONFIG, res.data.data, {
                root: true,
            })
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.get_data_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.CONFIG_BY_ID,
                loading: false,
            })
        }
    },
    async fetchDefaultCountryConfigList({ commit }, countryId: number) {
        commit(PricingPlansMutations.SET_LOADING, {
            type: LoadingTypes.COUNTRY_CONFIG_LIST,
            loading: true,
        })

        try {
            const res = await axios.get<AxiosResponse<PriceConfig[]>>(
                `${ApiRoutes.CONFIGS}/defaults?countryId=${countryId}`,
            )
            commit("setCountryDefaultConfigList", res.data.data)
        } catch (error) {
            console.log(error)
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.COUNTRY_CONFIG_LIST,
                loading: false,
            })
        }
    },
    async putOrPostConfig(
        { dispatch },
        { config, type }: { config: PriceConfig; type: "put" | "post" },
    ) {
        try {
            //todo: check if :id is needed for put
            await axios[type](ApiRoutes.CONFIGS, { data: config })
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        }
    },
    async putConfig({ dispatch }, config: PriceConfig) {
        try {
            await axios.put(`${ApiRoutes.CONFIGS}/${config.id}`, { data: config })
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.save_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )
        } catch (error) {
            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.save_error",
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )
        }
    },
    async fetchAdjustmentBreakdown(
        { commit },
        {
            adjustments,
            currencyCode,
            direction,
        }: { adjustments: SpotAdjustment[]; currencyCode: string; direction: string | undefined },
    ) {
        if (!adjustments?.length) {
            commit(PricingPlansMutations.SET_ADJUSTMENT_BREAKDOWN, null)
            return
        }

        commit(PricingPlansMutations.SET_LOADING, {
            type: LoadingTypes.ADJUSTMENT_BREAKDOWN,
            loading: true,
        })

        try {
            const mapped = adjustments.map((a: SpotAdjustment, i: number) => {
                if (a.adjustmentType === "CLAMP") {
                    return {
                        name: a.name,
                        adjustmentType: "CLAMP",
                        adjustmentAmountType: "FIXED",
                        clampMin: a.clampMin ?? null,
                        clampMax: a.clampMax ?? null,
                        currencyCode: a.currencyCode,
                        position: i,
                    }
                }
                return {
                    name: a.name,
                    adjustmentType: a.adjustmentType,
                    adjustmentAmountType: a.adjustmentAmountType,
                    amount: Number(a.amount),
                    currencyCode: a.currencyCode,
                    unit: a.unit,
                    position: i,
                }
            })

            const res = await axios.post(ApiRoutes.ADJUSTMENTS_BREAKDOWN, {
                data: {
                    currencyCode,
                    adjustments: mapped,
                    direction,
                },
            })

            commit(PricingPlansMutations.SET_ADJUSTMENT_BREAKDOWN, res.data.data.breakdown)
        } catch {
            commit(PricingPlansMutations.SET_ADJUSTMENT_BREAKDOWN, null)
        } finally {
            commit(PricingPlansMutations.SET_LOADING, {
                type: LoadingTypes.ADJUSTMENT_BREAKDOWN,
                loading: false,
            })
        }
    },
    async deleteConfig({ dispatch }, id: number) {
        try {
            await axios.delete(`${ApiRoutes.CONFIGS}/${id}`)

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message: "pricing_plans.delete_success",
                    color: "green",
                    isTranslation: true,
                },
                { root: true },
            )

            return true
        } catch (error: any) {
            let message = "pricing_plans.delete_error"

            if (error?.status === 409 && error.response?.data?.error?.message) {
                message = error.response?.data?.error?.message
            }

            dispatch(
                "toastMessageModule/showMessage",
                {
                    message,
                    color: "red",
                    isTranslation: true,
                },
                { root: true },
            )

            return false
        }
    },
}

const getters: GetterTree<PricingPlansState, RootState> = {
    configList: ({ configList }) => configList,
    loading: ({ loading }) => loading,
    timeplanConfigList: ({ timeplanConfigList }) => timeplanConfigList,
    priceLists: ({ priceLists }) => priceLists,
    priceList: ({ priceList }) => priceList,
    editConfig: ({ editConfig }) => editConfig,
    countryDefaultConfigList: ({ countryDefaultConfigList }) => countryDefaultConfigList,
    adjustmentBreakdown: ({ adjustmentBreakdown }) => adjustmentBreakdown,
    //todo: consider if this is needed
    // timeplanConfigList: ({ timeplanConfigList }) => {
    //     return (
    //         timeplanConfigList?.map((i) => {
    //             const item: PriceConfig = { ...i, timePlanBlockId: i.timePlanBlock?.id || null }
    //             return item
    //         }) || []
    //     )
    // },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
} as Module<PricingPlansState, RootState>
